import { useState, useEffect, useCallback } from "react";
import * as api from "../api/client";
import {
  Card, Btn, SectionTitle, Badge, ErrorBanner,
  SuccessBanner, Spinner, Empty, Table, TR, TD, Input, Select,
} from "../components/UI";

const fmtINR = n => n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", {minimumFractionDigits:2, maximumFractionDigits:2})}`;
const fmtDate = s => s && s !== "None" ? new Date(s).toLocaleDateString("en-IN") : "—";

// ── Role badge ─────────────────────────────────────────────────────────────
function RoleBadge({ user }) {
  if (user.is_admin)    return <span style={pill("#1E3A5F","#93C5FD")}>ADMIN</span>;
  if (user.is_readonly) return <span style={pill("#1E3A2F","#86EFAC")}>READ ONLY</span>;
  if (user.is_limited)  return <span style={pill("#3B1F5F","#C4B5FD")}>LIMITED</span>;
  return <span style={pill("#1C2B1C","#6EE7B7")}>ANALYST</span>;
}
const pill = (bg, color) => ({
  background:bg, color, padding:"2px 8px", borderRadius:4,
  fontSize:11, fontWeight:700, letterSpacing:"0.04em",
});

// ── User form ──────────────────────────────────────────────────────────────
function UserForm({ clients, onSave, onCancel, initial }) {
  const [form, setForm] = useState(initial || {
    email:"", password:"", full_name:"",
    is_admin:false, is_readonly:false, is_limited:false,
    allowed_client_ids:"",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const set = (k, v) => setForm(f => ({...f, [k]:v}));

  const submit = async () => {
    if (!form.email) { setError("Email is required"); return; }
    if (!initial && !form.password) { setError("Password is required"); return; }
    if (!initial && form.password.length < 8) { setError("Password must be at least 8 characters"); return; }
    setSaving(true); setError("");
    try { await onSave(form); }
    catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const roleOptions = [
    { value:"analyst",  label:"Analyst — view + edit RFP + ingest data" },
    { value:"limited",  label:"Limited — view + ingest data + alerts (no RFP edit)" },
    { value:"readonly", label:"Read Only — view and download only" },
    { value:"admin",    label:"Admin — full access including user management" },
  ];

  const currentRole = form.is_admin ? "admin" : form.is_readonly ? "readonly" : form.is_limited ? "limited" : "analyst";

  const applyRole = (role) => {
    set("is_admin",    role === "admin");
    set("is_readonly", role === "readonly");
    set("is_limited",  role === "limited");
  };

  return (
    <Card style={{ marginBottom:16, border:"2px solid #2563EB" }}>
      <div style={{ fontWeight:700, fontSize:15, color:"#111827", marginBottom:16 }}>
        {initial ? "Edit User" : "Create New User"}
      </div>
      <ErrorBanner message={error} onDismiss={() => setError("")}/>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
        <div>
          <div style={label}>Email address *</div>
          <input value={form.email} onChange={e => set("email", e.target.value)}
            disabled={!!initial}
            placeholder="user@company.com"
            style={{...inp, background: initial ? "#F9FAFB" : "#fff"}}/>
        </div>
        <div>
          <div style={label}>{initial ? "New password (leave blank to keep)" : "Password *"}</div>
          <input type="password" value={form.password || ""} onChange={e => set("password", e.target.value)}
            placeholder={initial ? "Leave blank to keep current" : "Min 8 characters"}
            style={inp}/>
        </div>
        <div>
          <div style={label}>Full name</div>
          <input value={form.full_name} onChange={e => set("full_name", e.target.value)}
            placeholder="First Last" style={inp}/>
        </div>
        <div>
          <div style={label}>Role</div>
          <select value={currentRole} onChange={e => applyRole(e.target.value)} style={inp}>
            {roleOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div style={{ gridColumn:"1/-1" }}>
          <div style={label}>Client access (select which clients this user can see)</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop:6 }}>
            <label style={checkItem}>
              <input type="checkbox"
                checked={!form.allowed_client_ids}
                onChange={() => set("allowed_client_ids", "")}
                style={{ marginRight:6 }}/>
              All clients
            </label>
            {clients.map(c => (
              <label key={c.id} style={checkItem}>
                <input type="checkbox"
                  checked={(form.allowed_client_ids || "").includes(c.id)}
                  onChange={e => {
                    const ids = (form.allowed_client_ids || "").split(",").filter(Boolean);
                    if (e.target.checked) {
                      set("allowed_client_ids", [...ids, c.id].join(","));
                    } else {
                      set("allowed_client_ids", ids.filter(id => id !== c.id).join(","));
                    }
                  }}
                  style={{ marginRight:6 }}/>
                {c.name}
              </label>
            ))}
          </div>
        </div>
      </div>
      <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
        <Btn onClick={onCancel} variant="secondary">Cancel</Btn>
        <Btn onClick={submit} loading={saving}>{initial ? "Save changes" : "Create user"}</Btn>
      </div>
    </Card>
  );
}
const label = { fontSize:12, fontWeight:600, color:"#374151", marginBottom:4 };
const inp   = { width:"100%", padding:"8px 12px", border:"1px solid #E5E7EB", borderRadius:8, fontSize:13, color:"#111827", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" };
const checkItem = { display:"flex", alignItems:"center", padding:"6px 12px", border:"1px solid #E5E7EB", borderRadius:6, fontSize:13, cursor:"pointer", background:"#fff" };

// ── Main AdminPanel ────────────────────────────────────────────────────────
export default function AdminPanel({ clientId, clientName }) {
  const [tab, setTab]             = useState("users");
  const [users, setUsers]         = useState([]);
  const [clients, setClients]     = useState([]);
  const [dataSummary, setDataSummary] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [success, setSuccess]     = useState("");
  const [showForm, setShowForm]   = useState(false);
  const [editUser, setEditUser]   = useState(null);
  const [deletingUser, setDeletingUser]   = useState(null);
  const [deletingMonth, setDeletingMonth] = useState(null);
  const [selectedClient, setSelectedClient] = useState(clientId || "");

  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(""), 3000); };

  // Load users
  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.admin.listUsers();
      setUsers(Array.isArray(r) ? r : []);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  // Load clients
  const loadClients = useCallback(async () => {
    try {
      const r = await api.clients.list();
      setClients(Array.isArray(r) ? r : []);
      if (!selectedClient && r.length > 0) setSelectedClient(r[0].id);
    } catch(e) {}
  }, []);

  // Load data summary for selected client
  const loadDataSummary = useCallback(async (cid) => {
    if (!cid) return;
    setLoading(true);
    try {
      const r = await api.admin.dataSummary(cid);
      setDataSummary(r);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadClients(); }, []);
  useEffect(() => { if (tab === "users") loadUsers(); }, [tab]);
  useEffect(() => { if (tab === "data" && selectedClient) loadDataSummary(selectedClient); }, [tab, selectedClient]);

  const createUser = async (form) => {
    await api.admin.createUser(form);
    showSuccess(`User ${form.email} created`);
    setShowForm(false);
    loadUsers();
  };

  const updateUser = async (form) => {
    await api.admin.updateUser(editUser.id, form);
    showSuccess("User updated");
    setEditUser(null);
    loadUsers();
  };

  const deleteUser = async (user) => {
    await api.admin.deleteUser(user.id);
    showSuccess(`User ${user.email} deleted`);
    setDeletingUser(null);
    loadUsers();
  };

  const deleteMonth = async (month) => {
    const r = await api.admin.deleteMonthData(selectedClient, month);
    showSuccess(`Deleted ${r.records_deleted} records for ${month}`);
    setDeletingMonth(null);
    loadDataSummary(selectedClient);
  };

  return (
    <div>
      <SectionTitle children="Admin Panel" sub="Manage users and ingested data"/>
      <ErrorBanner message={error} onDismiss={() => setError("")}/>
      {success && <SuccessBanner message={success}/>}

      {/* Tabs */}
      <div style={{ display:"flex", gap:4, marginBottom:20, borderBottom:"2px solid #F3F4F6" }}>
        {[
          { id:"users", label:"👤 User Management" },
          { id:"data",  label:"🗄 Data Management"  },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding:"8px 18px", border:"none", cursor:"pointer",
            fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600,
            background:"transparent",
            color: tab===t.id ? "#2563EB" : "#9CA3AF",
            borderBottom: tab===t.id ? "2px solid #2563EB" : "2px solid transparent",
            marginBottom:-2,
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── USER MANAGEMENT ─────────────────────────────────────────── */}
      {tab === "users" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:16 }}>
            <div style={{ fontSize:13, color:"#6B7280" }}>{users.length} user{users.length !== 1 ? "s" : ""} registered</div>
            <Btn onClick={() => { setShowForm(true); setEditUser(null); }} size="sm">+ Add user</Btn>
          </div>

          {showForm && !editUser && (
            <UserForm clients={clients} onSave={createUser} onCancel={() => setShowForm(false)}/>
          )}
          {editUser && (
            <UserForm clients={clients} initial={editUser} onSave={updateUser} onCancel={() => setEditUser(null)}/>
          )}

          {/* Delete confirmation */}
          {deletingUser && (
            <Card style={{ marginBottom:16, border:"2px solid #DC2626", background:"#FEF2F2" }}>
              <div style={{ fontWeight:700, color:"#DC2626", marginBottom:8 }}>⚠ Confirm deletion</div>
              <div style={{ fontSize:13, color:"#374151", marginBottom:16 }}>
                Delete user <strong>{deletingUser.email}</strong>? This cannot be undone.
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <Btn onClick={() => setDeletingUser(null)} variant="secondary" size="sm">Cancel</Btn>
                <Btn onClick={() => deleteUser(deletingUser)} variant="danger" size="sm">Delete user</Btn>
              </div>
            </Card>
          )}

          {loading && <div style={{ textAlign:"center", padding:32 }}><Spinner size={24}/></div>}
          {!loading && users.length === 0 && (
            <Empty icon="👤" title="No users" sub="Click '+ Add user' to create the first user"/>
          )}
          {!loading && users.length > 0 && (
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"#F8FAFC", borderBottom:"2px solid #E2E8F0" }}>
                  {["User","Role","Client access","Last login","Status","Actions"].map(h => (
                    <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontWeight:600, color:"#374151", fontSize:12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={u.id} style={{ borderBottom:"1px solid #F3F4F6", background: i%2===0?"#fff":"#FAFAFA" }}>
                    <td style={{ padding:"10px 12px" }}>
                      <div style={{ fontWeight:600, color:"#111827" }}>{u.full_name}</div>
                      <div style={{ fontSize:12, color:"#6B7280" }}>{u.email}</div>
                    </td>
                    <td style={{ padding:"10px 12px" }}><RoleBadge user={u}/></td>
                    <td style={{ padding:"10px 12px", fontSize:12, color:"#6B7280" }}>
                      {u.allowed_client_names?.length > 0 ? u.allowed_client_names.join(", ") : "All clients"}
                    </td>
                    <td style={{ padding:"10px 12px", fontSize:12, color:"#9CA3AF" }}>{fmtDate(u.last_login)}</td>
                    <td style={{ padding:"10px 12px" }}>
                      <span style={{ color: u.is_active ? "#16A34A" : "#DC2626", fontWeight:600, fontSize:12 }}>
                        {u.is_active ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td style={{ padding:"10px 12px" }}>
                      <div style={{ display:"flex", gap:6 }}>
                        <button onClick={() => { setEditUser(u); setShowForm(false); }}
                          style={{ padding:"4px 10px", borderRadius:6, border:"1px solid #E5E7EB", background:"#fff", cursor:"pointer", fontSize:12, color:"#374151" }}>
                          Edit
                        </button>
                        <button onClick={() => setDeletingUser(u)}
                          style={{ padding:"4px 10px", borderRadius:6, border:"1px solid #FCA5A5", background:"#FEF2F2", cursor:"pointer", fontSize:12, color:"#DC2626" }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── DATA MANAGEMENT ─────────────────────────────────────────── */}
      {tab === "data" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div style={{ fontSize:13, color:"#6B7280" }}>
              Select a client to view and manage their ingested data.
              Deleted data can be re-ingested from OCI API.
            </div>
            <select value={selectedClient} onChange={e => { setSelectedClient(e.target.value); setDataSummary(null); }}
              style={{ padding:"8px 12px", border:"1px solid #E5E7EB", borderRadius:8, fontSize:13, minWidth:200 }}>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Delete month confirmation */}
          {deletingMonth && (
            <Card style={{ marginBottom:16, border:"2px solid #DC2626", background:"#FEF2F2" }}>
              <div style={{ fontWeight:700, color:"#DC2626", marginBottom:8 }}>⚠ Confirm data deletion</div>
              <div style={{ fontSize:13, color:"#374151", marginBottom:16 }}>
                Delete all ingested data for <strong>{deletingMonth}</strong>?
                <br/>This cannot be undone. You can re-pull from OCI API after deletion.
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <Btn onClick={() => setDeletingMonth(null)} variant="secondary" size="sm">Cancel</Btn>
                <Btn onClick={() => deleteMonth(deletingMonth)} variant="danger" size="sm">Delete month data</Btn>
              </div>
            </Card>
          )}

          {loading && <div style={{ textAlign:"center", padding:32 }}><Spinner size={24}/></div>}

          {dataSummary && !loading && (
            <Card>
              <div style={{ fontWeight:700, fontSize:15, color:"#111827", marginBottom:4 }}>
                {dataSummary.client_name}
              </div>
              <div style={{ fontSize:13, color:"#6B7280", marginBottom:16 }}>
                {dataSummary.months.length} month{dataSummary.months.length !== 1 ? "s" : ""} of data ingested
              </div>

              {dataSummary.months.length === 0 && (
                <Empty icon="🗄" title="No data" sub="No months ingested for this client yet"/>
              )}

              {dataSummary.months.length > 0 && (
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                  <thead>
                    <tr style={{ background:"#F8FAFC", borderBottom:"2px solid #E2E8F0" }}>
                      {["Month","Records","Total cost (INR)","Last imported","Action"].map(h => (
                        <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontWeight:600, color:"#374151", fontSize:12 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dataSummary.months.map((m, i) => (
                      <tr key={m.month} style={{ borderBottom:"1px solid #F3F4F6", background: i%2===0?"#fff":"#FAFAFA" }}>
                        <td style={{ padding:"10px 12px", fontWeight:600, color:"#111827" }}>{m.month}</td>
                        <td style={{ padding:"10px 12px", color:"#6B7280" }}>{m.records}</td>
                        <td style={{ padding:"10px 12px", fontWeight:600 }}>{fmtINR(m.total_cost)}</td>
                        <td style={{ padding:"10px 12px", fontSize:12, color:"#9CA3AF" }}>{fmtDate(m.last_import)}</td>
                        <td style={{ padding:"10px 12px" }}>
                          <button onClick={() => setDeletingMonth(m.month)}
                            style={{ padding:"4px 12px", borderRadius:6, border:"1px solid #FCA5A5", background:"#FEF2F2", cursor:"pointer", fontSize:12, color:"#DC2626", fontWeight:600 }}>
                            🗑 Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
